<template>
  <div class="tenantManagementContainer">
    <div class="configurePageContainer" style="height: 100%; width: 100%">
      <div v-if="readPermission">
        <div v-if="isLoading" style="position: absolute; top: 50%; left: 65%; transform: translate(-50%, -50%); display: flex; justify-content: center; align-items: center">
          <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
        </div>
        <div v-if="!showAddForm" class="customRowTenant">
          <p class="jobHistoryTitle" style="text-align: left">{{ $t('departments.title') }}</p>
          <div class="searchBar">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
              <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <input type="text" class="searchInput" :placeholder="$t('departments.search_placeholder')" v-model="search" @input="handleSearch" />
          </div>
        </div>

        <!-- Search & Add Button -->
        <div v-if="!showAddForm" class="tableHeadingsNew">
          <div class="tableHeadingsNew" style="margin-top: 20px">
            <div class="customRow">
              <CustomDropdown :label="$t('departments.select_tenant')" :placeholder="$t('departments.select_tenant')" v-model="selectedTenantId" :options="tenantsWithMaster" :error="roleError" :disabled="userRole === 'Super Admin'" />
              <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />
              <span class="pageOfTitle">{{ $t('departments.pagination.page') }} {{ page }} {{ $t('departments.pagination.of') }} {{ totalPages }}</span>
              <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />
              <div class="pageDiv" @click.stop="togglePageSizeDropdown">
                <span class="pageDivText">{{ pageSize }}</span>
                <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />

                <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
                  <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                    {{ size }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div style="display: flex; gap: 17px">
            <Filter :tenant-id="selectedTenantId" @filter-applied="handleFilterChange" />
            <SecondaryButton v-if="writePermission" @click="handleAddDepartment">{{ $t('departments.add_button') }}</SecondaryButton>
          </div>
        </div>

        <!-- Add Department Form -->
        <div v-if="showAddForm" class="createDepartmentBody">
          <div class="createDepartmentHeader">
            <svg @click="cancelAdd" style="cursor: pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M5.25 11.25H20.25C20.45 11.25 20.64 11.33 20.78 11.47C20.92 11.61 21 11.8 21 12C21 12.2 20.92 12.39 20.78 12.53C20.64 12.67 20.45 12.75 20.25 12.75H5.25C5.05 12.75 4.86 12.67 4.72 12.53C4.58 12.39 4.5 12.2 4.5 12C4.5 11.8 4.58 11.61 4.72 11.47C4.86 11.33 5.05 11.25 5.25 11.25Z" fill="black" />
              <path d="M5.56 12L11.78 18.22C11.92 18.36 12 18.55 12 18.75C12 18.95 11.92 19.14 11.78 19.28C11.64 19.42 11.45 19.5 11.25 19.5C11.05 19.5 10.86 19.42 10.72 19.28L3.97 12.53C3.9 12.46 3.84 12.38 3.81 12.29C3.77 12.2 3.75 12.1 3.75 12C3.75 11.9 3.77 11.8 3.81 11.71C3.84 11.62 3.9 11.54 3.97 11.47L10.72 4.72C10.86 4.58 11.05 4.5 11.25 4.5C11.45 4.5 11.64 4.58 11.78 4.72C11.92 4.86 12 5.05 12 5.25C12 5.45 11.92 5.64 11.78 5.78L5.56 12Z" fill="black" />
            </svg>
            <span class="newPageTitle"> {{ editMode ? $t('departments.forms.edit_title') : $t('departments.forms.add_title') }} </span>
          </div>
          <div class="form-grid">
            <div class="form-field" v-show="!editMode || (editMode && newDepartment.tenantId)">
              <label>{{ $t('departments.forms.labels.tenant_name') }}</label>
              <select v-model="newDepartment.tenantId" :class="{ 'error-border': showErrors && errors.tenantId }" :disabled="true" v-show="!editMode || (editMode && newDepartment.tenantId)">
                <!-- <option value="" disabled>Select Tenant</option> -->
                <option v-if="userRole === 'Super Admin'" value="" selected>{{ $t('departments.forms.options.master_tenant') }}</option>
                <option v-for="tenant in tenants" :value="tenant.value" :key="tenant.value">
                  {{ tenant.label }}
                </option>
              </select>
              <span class="error-text" v-if="showErrors && errors.tenantId">
                {{ errors.tenantId }}
              </span>
            </div>
            <div class="form-field">
              <label>{{ $t('departments.forms.labels.primary_classification') }}</label>
              <select
                v-model="newDepartment.categoryId"
                :class="{
                  'error-border': showErrors && errors.categoryId,
                }"
              >
                <option value="">{{ $t('departments.forms.placeholders.select') }}</option>
                <option v-for="category in activeCategories" :key="category.id" :value="category.id">
                  {{ category.category_name }}
                </option>
              </select>
              <span class="error-text" v-if="showErrors && errors.categoryId">
                {{ errors.categoryId }}
              </span>
            </div>

            <div class="form-field">
              <label>{{ $t('departments.forms.labels.sub_category_name') }}</label>
              <template v-if="newDepartment.tenantId != ''">
                <select
                  v-model="newDepartment.departmentId"
                  :class="{
                    'error-border': showErrors && errors.departmentId,
                  }"
                >
                  <option value="">{{ $t('departments.forms.placeholders.select_sub_category') }}</option>
                  <option v-for="type in tenantDepartments" :value="type.id" :key="type.id">
                    {{ type.department_name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.departmentId">
                  {{ errors.departmentId }}
                </span>
              </template>
              <template v-else>
                <input
                  type="text"
                  v-model="newDepartment.name"
                  :class="{
                    'error-border': showErrors && errors.name,
                  }"
                  :placeholder="$t('departments.forms.placeholders.enter_document_type')"
                />
                <span class="error-text" v-if="showErrors && errors.name">
                  {{ errors.name }}
                </span>
              </template>
            </div>
            <div class="form-field" v-if="newDepartment.tenantId != ''">
              <label>{{ $t('departments.forms.labels.sub_category_display_name') }}</label>
              <input
                type="text"
                v-model="newDepartment.departmentDisplayName"
                :class="{
                  'error-border': showErrors && errors.departmentDisplayName,
                }"
                :placeholder="$t('departments.forms.placeholders.enter_sub_category_display_name')"
              />
              <span class="error-text" v-if="showErrors && errors.departmentDisplayName">
                {{ errors.departmentDisplayName }}
              </span>
            </div>
            <div class="form-field">
              <label>{{ $t('departments.forms.labels.status') }}</label>
              <IsActiveComponentV1 v-model="newDepartment.isActive" @change="handleStatusChange" />
            </div>
          </div>
          <div class="button-group">
            <button class="cancel-btn" @click="cancelAdd">{{ $t('departments.buttons.cancel') }}</button>
            <button v-if="writePermission" class="save-btn" @click="saveDepartment">{{ $t('departments.buttons.save') }}</button>
          </div>
        </div>

        <!-- Departments Table -->
        <div v-if="!showAddForm" class="tableContainer" style="margin-top: 20px">
          <table class="customTable">
            <thead class="customTableHeader">
              <tr class="customTableRow">
                <th>{{ $t('departments.table.sr_no') }}</th>
                <th>{{ $t('departments.table.tenant_name') }}</th>
                <th>{{ $t('departments.table.primary_classification') }}</th>
                <th>{{ $t('departments.table.sub_category') }}</th>
                <th>{{ $t('departments.table.is_active') }}</th>
                <th>{{ $t('departments.table.action') }}</th>
              </tr>
            </thead>
            <tbody class="customTableBody">
              <tr v-if="!filteredDepartments || filteredDepartments.length === 0" style="height: 50px !important">
                <td colspan="6" style="text-align: center; color: #666">No data available</td>
              </tr>
              <tr v-else v-for="(department, index) in filteredDepartments" :key="department.id" style="height: 50px !important">
                <td>{{ index + 1 }}</td>
                <td>{{ department?.tenant?.tenant_name || 'NA' }}</td>
                <td>{{ department?.category?.category_name }}</td>
                <td>
                  {{ department?.department_display_name ? department?.department_display_name : department?.department_name }}
                </td>
                <td>
                  <div class="toggle-wrapper">
                    <CustomToggle :modelValue="department.is_active" @update:modelValue="(value) => handleStatusToggle(department, value)" :disabled="!writePermission" />
                  </div>
                </td>
                <td>
                  <CustomHyperlink @click="editDepartment(department)">{{ $t('departments.actions.edit') }}</CustomHyperlink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else>
        <PermissionAlert :message="$t('departments.no_permission')" />
      </div>
    </div>
    <ConfirmationModal :show="showConfirmModal" :message="$t('departments.modals.confirm_status.message', { departmentName: pendingStatusChange?.field?.department_display_name ? pendingStatusChange?.field?.department_display_name : pendingStatusChange?.field?.department_name })" @close="closeConfirmModal" @confirm="confirmStatusChange" />
  </div>
</template>

<script>
import { get, put, post } from '@/utils/api'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import CustomHyperlink from '@/components/CustomHyperlink/CustomHyperlink.vue'
import SecondaryButton from '@/components/SecondaryButton/SecondaryButton.vue'
import Filter from './Filter.vue'
import IsActiveComponentV1 from '@/components/IsActiveComponentV1/IsActiveComponentV1.vue'
import { showToast } from '@/utils/toast'
import CustomDropdown from '@/components/CustomDropdown/CustomDropdown.vue'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import tenantSelectionStore from '@/stores/tenantSelectionStore'

export default {
  components: {
    CustomToggle,
    CustomHyperlink,
    SecondaryButton,
    Filter,
    IsActiveComponentV1,
    CustomDropdown,
    ConfirmationModal,
    PermissionAlert,
  },
  data() {
    return {
      readPermission: false,
      writePermission: false,
      readPermissionMaster: false,
      writePermissionMaster: false,
      loadingStates: {
        tenants: false,
        categories: false,
        departments: false,
        saveDepartment: false,
        isPopulating: false,
        fetchDepartments: false,
      },
      loading: false,
      selectedTenantId: '',
      tenantStoreUnsubscribe: null,
      departments: [],
      tenantDepartments: [],
      categories: [], // To store categories for dropdown
      search: '',
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      showAddForm: false,
      userRole: localStorage.getItem('userRole'),
      newDepartment: {
        categoryId: '',
        name: '',
        isActive: true,
        tenantId: '',
        departmentId: '',
        departmentDisplayName: '',
      },
      editMode: false,
      editId: null,
      tenants: [],
      tenantsWithMaster: [],
      showErrors: false,
      errors: {
        tenantId: '',
        categoryId: '',
        departmentId: '',
        departmentDisplayName: '',
        name: '',
      },
      activeFilters: null,
      pendingStatusChange: null,
      showConfirmModal: false,
      debounceTimeout: null,
    }
  },
  mounted() {
    document.title = this.$t('departments.title')
    this.checkPermissions()

    // PURE STORE APPROACH: Get current state from store
    const storeState = tenantSelectionStore.getCurrentState()

    this._isStoreUpdate = true
    this.selectedTenantId = storeState.selectedTenantId || ''
    this._isStoreUpdate = false

    // Subscribe to tenant selection store
    this.tenantStoreUnsubscribe = tenantSelectionStore.subscribe((state) => {
      const previousTenantId = this.selectedTenantId

      // Set flag to prevent watcher from triggering store update
      this._isStoreUpdate = true
      this.selectedTenantId = state.selectedTenantId || ''
      this._isStoreUpdate = false

      // Only fetch data if tenant actually changed and it's not the initial load
      if (previousTenantId !== state.selectedTenantId && previousTenantId !== '') {
        this.fetchDepartments()
      }
    })

    // First fetch tenants to populate dropdown, then fetch departments
    this.$nextTick(async () => {
      await this.fetchTenants()
      await this.fetchDepartments()
    })

    document.addEventListener('click', this.handleClickAway)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
    // Unsubscribe from tenant selection store
    if (this.tenantStoreUnsubscribe) {
      this.tenantStoreUnsubscribe()
    }
  },
  computed: {
    filteredDepartments() {
      if (!this.departments || this.departments.length === 0) {
        return []
      }

      // Always filter out Bedrock Training and Bedrock Documents departments
      // (Non-Bedrock tenants won't reach this page through navigation anyway)
      return this.departments.filter((department) => {
        const departmentName = department.department_display_name || department.department_name || ''
        return departmentName !== 'Bedrock Training' && departmentName !== 'Bedrock Documents' && departmentName !== 'Textract Training' && departmentName !== 'Textract Documents'
      })
    },
    isLoading() {
      return Object.values(this.loadingStates).some((state) => state)
    },
    activeCategories() {
      return this.categories.filter((category) => {
        const condition = (category.is_active === true || category.id === this.newDepartment.categoryId) && category.category_name !== 'Bedrock Training' && category.category_name !== 'Bedrock Documents' && category.category_name !== 'Textract Training'
        if (condition) {
          return category
        }
      })
    },
    selectedTenantLabel() {
      if (!this.selectedTenantId || !this.tenantsWithMaster) return ''
      const selectedTenant = this.tenantsWithMaster.find((tenant) => tenant.value === this.selectedTenantId)
      return selectedTenant ? selectedTenant.label : ''
    },
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      // Check for master tenant permissions
      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)

      // Check for tenant permissions
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }
    },
    handleStatusToggle(field, newValue) {
      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModal = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.pendingStatusChange = null
    },
    async confirmStatusChange() {
      if (!this.pendingStatusChange) return
      const { field, newStatus } = this.pendingStatusChange
      const updatedField = this.departments.find((f) => f.id === field.id)
      if (updatedField) {
        updatedField.is_active = newStatus
        this.updateDepartmentStatus(updatedField)
        //  await this.updatedField)
      }
      this.closeConfirmModal()
      this.pendingStatusChange = null
    },
    async updateDepartmentStatus(selectedDepartmentType) {
      if (!selectedDepartmentType) return
      this.loadingStates.saveDepartment = true
      try {
        const payload = {
          category_id: selectedDepartmentType.category_id,
          department_name: selectedDepartmentType.department_name,
          is_active: Boolean(selectedDepartmentType.is_active),
        }

        if (selectedDepartmentType.tenant_id) {
          delete payload.department_name
          payload.department_id = selectedDepartmentType.department_id
          payload.tenant_id = selectedDepartmentType.tenant_id
          payload.department_display_name = selectedDepartmentType.department_display_name
        }
        payload.created_by = selectedDepartmentType.created_by // Add created_by only in edit mode
        let response
        if (payload.tenant_id) {
          response = await put(`/v1/tenant-departments/${selectedDepartmentType.id}`, payload)
        } else {
          response = await put(`/v1/departments/${selectedDepartmentType.id}`, payload)
        }
        if (response?.data?.code === 200 || response?.data?.code === 201) {
          showToast({
            title: this.$t('departments.messages.success_title'),
            message: selectedDepartmentType.is_active ? this.$t('departments.messages.success.status_enabled') : this.$t('departments.messages.success.status_disabled'),
            type: 'success',
          })
        } else {
          throw new Error(response?.data?.message || this.$t('departments.messages.error.update_failed'))
        }
      } catch (err) {
        showToast({
          title: this.$t('departments.messages.error_title'),
          message: err.message || this.$t('departments.messages.error.status_update_failed'),
          type: 'error',
        })
      } finally {
        this.loadingStates.saveDepartment = false
      }
    },
    handleSearch() {
      // Clear existing timeout
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      // Set new timeout
      this.debounceTimeout = setTimeout(() => {
        this.page = 1 // Reset to first page when searching
        this.fetchDepartments()
      }, 300) // 300ms debounce
    },
    async fetchCategories() {
      try {
        const response = await get('/v1/categories?page=1&pageSize=1000')
        if (response?.data?.code === 200) {
          this.categories = (response.data.data.data || []).map((category) => ({
            id: category.id,
            category_name: category.category_name,
            is_active: category.is_active,
          }))
        }
      } catch (err) {}
    },
    async fetchDepartments() {
      // Prevent multiple simultaneous calls
      if (this.loadingStates.fetchDepartments) {
        return
      }

      this.loadingStates.fetchDepartments = true
      try {
        let url = `/v1/departments?page=${this.page}&pageSize=${this.pageSize}`
        if (this.selectedTenantId != 'master') {
          url = `/v1/tenant-departments?page=${this.page}&pageSize=${this.pageSize}&tenantId=${this.selectedTenantId}`
        }
        // const response = await get(
        //   `/v1/departments?page=${this.page}&pageSize=${this.pageSize}&isActive=true`
        // )
        if (this.search) {
          url += `&search=${encodeURIComponent(this.search)}`
        }
        if (this.activeFilters) {
          Object.keys(this.activeFilters).forEach((key) => {
            const value = this.activeFilters[key]
            if (value !== null && value !== undefined && value !== '') {
              url += `&${key}=${encodeURIComponent(value)}`
            }
          })
        }
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.departments = response.data.data.data
          this.total = response.data.data.total
          this.totalPages = Math.ceil(this.total / this.pageSize)
        }
      } catch (err) {
      } finally {
        this.loadingStates.fetchDepartments = false
      }
    },
    async fetchTenantCategories(tenantId) {
      // Prevent multiple simultaneous calls
      if (this.loadingStates.tenantCategories || !tenantId) {
        return
      }

      this.loadingStates.tenantCategories = true
      try {
        const response = await get(`/v1/tenant-categories?tenantId=${tenantId}&page=1&pageSize=100000`)
        if (response?.data?.code === 200) {
          this.categories = (response.data.data.data || []).map((category) => ({
            id: category.category_id,
            category_name: category.category_display_name,
            is_active: category.is_active,
          }))
        }
      } catch (err) {
        console.error('📋 Departments: Error fetching tenant categories:', err)
      } finally {
        this.loadingStates.tenantCategories = false
      }
    },
    async fetchTenantDepartments(categoryId) {
      // Prevent multiple simultaneous calls
      if (this.loadingStates.departments || !categoryId) {
        return
      }

      this.loadingStates.departments = true
      try {
        const url = `/v1/departments?categoryId=${categoryId}&page=1&pageSize=100000`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.tenantDepartments = (response.data.data.data || []).map((dept) => ({
            id: dept.id,
            department_name: dept.department_name,
            department_display_name: dept.department_display_name,
          }))
        }
      } catch (err) {
        console.error('📋 Departments: Error fetching tenant departments:', err)
        this.tenantDepartments = []
      } finally {
        this.loadingStates.departments = false
      }
    },
    async fetchTenants() {
      this.loadingStates.tenants = true
      try {
        const res = await get('/v1/tenants?page=1&pageSize=1000')
        if (res?.data?.code === 200) {
          this.tenants = (res.data.data.tenants || []).map((tenant) => ({
            label: tenant.tenant_name,
            value: tenant.id,
          }))

          // Build tenants list based on permissions
          if (this.readPermissionMaster) {
            if (this.userRole === 'Super Admin') {
              this.tenantsWithMaster = [{ label: 'Master Tenant', value: 'master' }, ...this.tenants]

              // Only set selectedTenantId if it hasn't been initialized by the store
              if (!this.selectedTenantId) {
                this._isStoreUpdate = true
                this.selectedTenantId = 'master'
                this._isStoreUpdate = false
              } else {
              }
            } else {
              this.tenantsWithMaster = this.tenants

              // Only set selectedTenantId if it hasn't been initialized by the store
              if (!this.selectedTenantId && this.tenants.length > 0) {
                this._isStoreUpdate = true
                this.selectedTenantId = this.tenants[0].value
                this._isStoreUpdate = false
              } else {
              }
            }
          } else {
            this.tenantsWithMaster = this.tenants

            // Only set selectedTenantId if it hasn't been initialized by the store
            if (!this.selectedTenantId && this.tenants.length > 0) {
              this._isStoreUpdate = true
              this.selectedTenantId = this.tenants[0].value
              this._isStoreUpdate = false
            } else {
            }
          }

          // Set newDepartment.tenantId for form initialization (only affects the add form)
          if (!this.editMode) {
            if (this.userRole === 'Super Admin' && this.readPermissionMaster) {
              this.newDepartment.tenantId = 'master'
            } else if (this.tenants.length > 0) {
              this.newDepartment.tenantId = this.tenants[0].value
            }
          }
        }
      } catch (err) {
        console.error('🏢 Departments: Error fetching tenants:', err)
      } finally {
        this.loadingStates.tenants = false
      }
    },
    handleAddDepartment() {
      this.showAddForm = true
      this.resetForm()
      this.editId = null
      this.editMode = false

      // Initialize tenant from store
      this.newDepartment.tenantId = this.selectedTenantId === 'master' ? '' : this.selectedTenantId

      // Only fetch categories if needed (tenants already loaded)
      if (this.selectedTenantId === 'master' || !this.selectedTenantId) {
        this.fetchCategories()
      } else {
        this.fetchTenantCategories(this.selectedTenantId)
      }
    },
    resetForm() {
      // Initialize tenant from store for consistency
      const tenantIdFromStore = this.selectedTenantId === 'master' ? '' : this.selectedTenantId

      this.newDepartment = {
        tenantId: tenantIdFromStore,
        categoryId: '',
        departmentId: '',
        departmentDisplayName: '',
        name: '',
        isActive: true,
      }
      this.categories = []
      this.tenantDepartments = []
    },
    cancelAdd() {
      this.resetForm()
      this.showAddForm = false
    },
    editDepartment(department) {
      this.editMode = true
      this.editId = department.id
      this.clearErrors()
      this.populateFormData(department)
      this.showAddForm = true
    },
    clearErrors() {
      this.errors = {}
      this.showErrors = false
    },
    async populateFormData(docType) {
      // Prevent watch triggers during population
      this.loadingStates.isPopulating = true

      try {
        if (docType.tenant_id) {
          // Fetch categories first, then departments
          await this.fetchTenantCategories(docType.tenant_id)
          if (docType.category_id) {
            await this.fetchTenantDepartments(docType.category_id)
          }
        } else {
          await this.fetchCategories()
        }

        // Set form data after API calls complete
        this.newDepartment = {
          tenantId: docType.tenant_id || '',
          categoryId: docType.category_id || '',
          departmentId: docType.department_id || '',
          name: docType.department_name || '',
          isActive: Boolean(docType.is_active),
          created_by: docType.created_by || null,
          departmentDisplayName: docType.department_display_name,
        }
      } catch (error) {
        console.error('📋 Departments: Error populating form data:', error)
      } finally {
        // Re-enable watchers after population
        this.$nextTick(() => {
          this.loadingStates.isPopulating = false
        })
      }
    },
    validateForm() {
      let isValid = true
      this.errors = {
        tenantId: '',
        categoryId: '',
        departmentId: '',
        departmentDisplayName: '',
        name: '',
      }
      if (this.newDepartment.tenantId) {
        // Tenant department validation
        if (!this.newDepartment.departmentId) {
          this.errors.departmentId = this.$t('departments.forms.validation.department_id_required')
          isValid = false
        }
        if (!this.newDepartment.departmentDisplayName?.trim()) {
          this.errors.departmentDisplayName = this.$t('departments.forms.validation.department_display_name_required')
          isValid = false
        }
      } else {
        // Master department validation
        if (!this.newDepartment.name?.trim()) {
          this.errors.name = this.$t('departments.forms.validation.name_required')
          isValid = false
        }
      }

      if (!this.newDepartment.categoryId) {
        this.errors.categoryId = this.$t('departments.forms.validation.category_id_required')
        isValid = false
      }

      return isValid
    },

    async saveDepartment() {
      this.loadingStates.saveDepartment = true
      this.showErrors = true

      if (!this.validateForm()) {
        this.loadingStates.saveDepartment = false
        return
      }

      try {
        const payload = {
          category_id: this.newDepartment.categoryId,
          department_name: this.newDepartment.name,
          is_active: Boolean(this.newDepartment.isActive),
        }

        if (this.newDepartment.tenantId) {
          delete payload.department_name
          payload.department_id = this.newDepartment.departmentId
          payload.tenant_id = this.newDepartment.tenantId
          payload.department_display_name = this.newDepartment.departmentDisplayName
        }

        let response

        if (this.editMode) {
          payload.created_by = this.newDepartment.created_by // Add created_by only in edit mode
          if (payload.tenant_id) {
            response = await put(`/v1/tenant-departments/${this.editId}`, payload)
          } else {
            response = await put(`/v1/departments/${this.editId}`, payload)
          }
        } else {
          if (payload.tenant_id) {
            response = await post('/v1/tenant-departments', payload)
          } else {
            response = await post('/v1/departments/', payload)
          }
        }

        if (response?.data?.code === 200 || response?.data?.code === 201) {
          showToast({
            title: this.$t('departments.messages.success_title'),
            message: this.editMode ? this.$t('departments.messages.success.updated') : this.$t('departments.messages.success.created'),
            type: 'success',
          })
          this.loadingStates.saveDepartment = false
          this.showAddForm = false
          this.editMode = false
          this.editId = null
          await this.fetchDepartments()
        } else {
          throw new Error(response?.data?.message || (this.editMode ? this.$t('departments.messages.error.update_failed') : this.$t('departments.messages.error.create_failed')))
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message
        if (errorMessage.toLowerCase().includes('unique constraint')) {
          showToast({
            title: this.$t('departments.messages.error_title'),
            message: this.$t('departments.messages.error.duplicate_exists'),
            type: 'error',
          })
        } else {
          showToast({
            title: this.$t('departments.messages.error_title'),
            message: errorMessage || (this.editMode ? this.$t('departments.messages.error.update_failed') : this.$t('departments.messages.error.create_failed')),
            type: 'error',
          })
        }
      } finally {
        this.loadingStates.saveDepartment = false
      }

      // Implement save department logic here
      // this.showAddForm = false
      // await this.fetchDepartments()
    },
    // write a function to handle add/edit department
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchDepartments()
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchDepartments()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchDepartments()
      }
    },
    handleFilterChange(filters) {
      this.activeFilters = filters
      this.fetchDepartments()
      // Implement filter logic here
    },
    handleStatusChange(value) {
      this.newDepartment.isActive = value
    },
    async handleFieldChange(fieldName, value) {
      // Centralized error handling
      if (this.errors[fieldName]) {
        this.errors[fieldName] = ''
      }

      // Handle specific field changes
      switch (fieldName) {
        case 'categoryId':
          if (!this.loadingStates.isPopulating && value) {
            if (this.newDepartment.tenantId) {
              await this.fetchTenantDepartments(value)
            }
            this.newDepartment.departmentId = ''
            this.newDepartment.departmentDisplayName = ''
          } else if (!value) {
            this.tenantDepartments = []
          }
          break

        case 'tenantId':
          if (!this.loadingStates.isPopulating) {
            if (value) {
              this.newDepartment.categoryId = ''
              this.newDepartment.departmentId = ''
              this.tenantDepartments = []
              this.newDepartment.name = ''
              await this.fetchTenantCategories(value)
            } else {
              this.newDepartment.departmentDisplayName = ''
              this.newDepartment.categoryId = ''
              this.newDepartment.name = ''
              this.categories = []
              this.tenantDepartments = []
              this.fetchCategories()
              this.newDepartment.tenantId = 'master'
            }
          }
          break

        case 'name':
        case 'departmentDisplayName':
          break
        // These fields only need error clearing which is handled at the start

        default:
      }

      // Update the model
      if (typeof value !== 'undefined') {
        this.newDepartment[fieldName] = value
      }
    },
  },
  watch: {
    async selectedTenantId(newValue, oldValue) {
      // Only react if this is a manual change (not from store subscription)
      if (newValue === oldValue || this._isStoreUpdate) return

      this.page = 1
      this.activeFilters = null

      // Update the store with new tenant ID (will call API to check AI method)
      await tenantSelectionStore.setSelectedTenantId(newValue)
    },
    'newDepartment.tenantId'(value, oldValue) {
      // Only trigger if this is not during population and values actually changed
      if (!this.loadingStates.isPopulating && value !== oldValue) {
        this.handleFieldChange('tenantId', value)
      }
    },
    'newDepartment.categoryId'(value, oldValue) {
      // Only trigger if this is not during population and values actually changed
      if (!this.loadingStates.isPopulating && value !== oldValue) {
        this.handleFieldChange('categoryId', value)
      }
    },
    'newDepartment.name'(value) {
      this.handleFieldChange('name', value)
    },
    'newDepartment.departmentDisplayName'(value) {
      this.handleFieldChange('departmentDisplayName', value)
    },
    'newDepartment.departmentId'(value) {
      this.handleFieldChange('departmentId', value)
      if (value) {
        const selected = this.tenantDepartments.find((d) => d.id === value)
        if (selected) {
          this.newDepartment.departmentDisplayName = selected.department_display_name || selected.department_name || ''
        }
      }
    },
    // Watch for tenant options loading to ensure consistent dropdown display
    tenantsWithMaster: {
      handler(newTenants) {
        // Ensure selectedTenantId is valid when tenants are loaded
        if (newTenants && newTenants.length > 0 && this.selectedTenantId) {
          const isValidSelection = newTenants.some((tenant) => tenant.value === this.selectedTenantId)
          if (!isValidSelection) {
            this._isStoreUpdate = true
            if (this.userRole === 'Super Admin' && this.readPermissionMaster) {
              this.selectedTenantId = 'master'
            } else if (newTenants.length > 0) {
              this.selectedTenantId = newTenants[0].value
            }
            this._isStoreUpdate = false
          }
        }
      },
      deep: true,
      immediate: false,
    },
  },
}
</script>

<style scoped>
/* Reuse the same styles as Categories.vue */
.createDepartmentBody {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.createDepartmentHeader {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.createDepartmentHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.searchBar {
  display: flex;
  align-items: center;
  width: 386px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background: #fff;
  padding: 0 10px;
  gap: 10px;
}

.searchInput {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #8f969b;
}

.button-group {
  margin-top: 20px;
  display: flex;
  gap: 20px;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}

.save-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 25px;
  margin-bottom: 25px;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.form-field select:disabled {
  background-color: #f5f5f5;
  color: #999999;
  cursor: not-allowed;
  opacity: 0.6;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.error-border {
  border-color: #dc3545 !important;
}
</style>
